'use client';

import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { X, Save, Trash2, Crown, Check, AlertTriangle } from 'lucide-react';
import { Group } from '@/types';
import { cn } from '@/lib/utils';
import { useAuthContext } from '@/components/providers/AuthProvider';
import type { GroupRole } from '@/lib/groups/authority';

interface GroupSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  group: Group;
  // Viewer's resolved authority from GET /api/groups/[id] — never re-derived
  // from raw role columns in this component.
  viewerRole: GroupRole | null;
  // Called with the fresh group from the PATCH response so the page can sync.
  onSaved: (updated: Group) => void;
  // Called after a successful soft-delete so the page can redirect to /groups.
  onDeleted: () => void;
}

type Privacy = 'public' | 'request' | 'private';
const PRIVACY_VALUES: Privacy[] = ['public', 'request', 'private'];

// The 7 curated banner palettes (CHECK constraint on groups.banner).
const BANNER_OPTIONS = [
  'default',
  'slate',
  'twilight',
  'velvet',
  'ember',
  'moss',
  'rose',
] as const;

function normalizePrivacy(p: string | null | undefined): Privacy {
  return p === 'private' || p === 'request' ? p : 'public';
}

type PendingRequest = {
  id: string;
  userId: string;
  message: string | null;
  createdAt: string;
  user: { id: string; name: string; username: string; avatar: string | null } | null;
};

export default function GroupSettingsModal({
  isOpen,
  onClose,
  group,
  viewerRole,
  onSaved,
  onDeleted,
}: GroupSettingsModalProps) {
  const { user } = useAuthContext();

  const [formData, setFormData] = useState({
    name: group.name ?? '',
    description: group.description ?? '',
    tags: Array.isArray(group.tags) && group.tags.length > 0 ? group.tags.join(', ') : '',
    privacy: normalizePrivacy(group.privacy),
  });
  const [banner, setBanner] = useState<string>(group.banner ?? 'default');
  const [rules, setRules] = useState<string>(group.rules ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const [requestsLoaded, setRequestsLoaded] = useState(false);
  const [busyReqUserId, setBusyReqUserId] = useState<string | null>(null);
  const [requestsError, setRequestsError] = useState<string | null>(null);

  // Gating — sections by the viewer's resolved authority.
  // General / Banner / Rules / Requests: owner or admin. Danger Zone: owner only.
  const canEdit = viewerRole === 'owner' || viewerRole === 'admin';
  const isOwner = viewerRole === 'owner';

  // Load pending join requests for admins when the modal opens.
  const loadRequests = useCallback(async () => {
    try {
      const res = await fetch(`/api/groups/${group.id}/requests`, { cache: 'no-store' });
      if (res.ok) {
        const data = await res.json();
        setPendingRequests(Array.isArray(data) ? data : []);
      }
    } catch {
      // non-critical
    } finally {
      setRequestsLoaded(true);
    }
  }, [group.id]);

  useEffect(() => {
    if (isOpen && canEdit) void loadRequests();
  }, [isOpen, canEdit, loadRequests]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!canEdit) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const tags = formData.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0)
        .slice(0, 5);

      // Privacy is constrained to the DB CHECK set — never send anything else.
      const privacy: Privacy = PRIVACY_VALUES.includes(formData.privacy)
        ? formData.privacy
        : 'public';

      const res = await fetch(`/api/groups/${group.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user?.id ?? '',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          description: formData.description.trim(),
          tags,
          privacy,
          banner,
          // Allow empty → null. Content itself is preserved as typed.
          rules: rules.trim().length === 0 ? null : rules,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSaveError(data?.error || `Failed to save changes (${res.status})`);
        return;
      }

      // Update from the server response, not an optimistic guess.
      onSaved(data as Group);
      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save changes.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isOwner) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const res = await fetch(`/api/groups/${group.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user?.id ?? '',
        },
        // Soft-delete only — RLS hides status='deleted' from listings/detail.
        // TODO: notify members (out of scope here).
        body: JSON.stringify({ status: 'deleted' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setDeleteError(data?.error || `Failed to delete group (${res.status})`);
        return;
      }
      onDeleted();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete group.');
    } finally {
      setIsDeleting(false);
    }
  };

  const decideRequest = async (userId: string, action: 'approve' | 'deny') => {
    setBusyReqUserId(userId);
    setRequestsError(null);
    const snapshot = pendingRequests;
    // Optimistic: drop the row from the pending list either way.
    setPendingRequests((prev) => prev.filter((r) => r.userId !== userId));
    try {
      const res = await fetch(`/api/groups/${group.id}/requests/${userId}/${action}`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error();
    } catch {
      setPendingRequests(snapshot);
      setRequestsError(`Could not ${action} the request. Please try again.`);
    } finally {
      setBusyReqUserId(null);
    }
  };

  const deleteArmed = deleteText.trim() === group.name.trim();

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-surface-2 rounded-xl border border-border-subtle max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border-subtle">
          <h2 className="text-xl font-semibold text-text-primary flex items-center">
            <Crown className="w-5 h-5 mr-2 text-accent" />
            Group Settings
          </h2>
          <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!showDeleteConfirm ? (
          <div className="p-6 space-y-6">
            {/* Basic Info */}
            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4">Basic Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Group Name *
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter group name"
                    disabled={!canEdit}
                    className="bg-surface-3 border-border-default text-text-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Description *
                  </label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, description: e.target.value }))
                    }
                    placeholder="Describe your group"
                    disabled={!canEdit}
                    className="bg-surface-3 border-border-default text-text-primary min-h-[100px]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">Tags</label>
                  <Input
                    value={formData.tags}
                    onChange={(e) => setFormData((prev) => ({ ...prev, tags: e.target.value }))}
                    placeholder="voice-acting, networking, etc."
                    disabled={!canEdit}
                    className="bg-surface-3 border-border-default text-text-primary"
                  />
                  <p className="text-xs text-text-tertiary mt-1">
                    Separate tags with commas (max 5)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">Privacy</label>
                  <select
                    value={formData.privacy}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        privacy: e.target.value as Privacy,
                      }))
                    }
                    disabled={!canEdit}
                    className="w-full bg-surface-3 border border-border-default text-text-primary rounded-lg px-3 py-2"
                  >
                    <option value="public">Public</option>
                    <option value="request">Request to join</option>
                    <option value="private">Private</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Banner — curated palette enum. Each swatch is a live preview via
                data-banner + var(--hdr-bg), so it renders correctly in both
                Booth and Daylight. */}
            <div className="border-t border-border-subtle pt-6">
              <h3 className="text-lg font-semibold text-text-primary mb-1">Banner</h3>
              <p className="text-xs text-text-tertiary mb-4">
                A curated palette for the group header.
              </p>
              <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                {BANNER_OPTIONS.map((opt) => {
                  const selected = banner === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => canEdit && setBanner(opt)}
                      disabled={!canEdit}
                      aria-pressed={selected}
                      title={opt}
                      className={cn(
                        'relative h-14 rounded-md overflow-hidden border transition-all',
                        selected
                          ? 'border-accent ring-1 ring-accent'
                          : 'border-border-subtle hover:border-border-default',
                        !canEdit && 'opacity-60 cursor-not-allowed',
                      )}
                    >
                      <span
                        data-banner={opt}
                        aria-hidden
                        className="absolute inset-0"
                        style={{ background: 'var(--hdr-bg)' }}
                      />
                      {selected && (
                        <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-accent text-text-on-accent flex items-center justify-center">
                          <Check className="w-2.5 h-2.5" strokeWidth={3} />
                        </span>
                      )}
                      <span className="absolute bottom-0 inset-x-0 text-[10px] font-mono text-text-primary bg-surface-0/70 px-1 py-0.5 capitalize truncate">
                        {opt}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Rules — owner/admin. Single text blob surfaced on the request
                screen for request-privacy groups. Empty = no rules. */}
            <div className="border-t border-border-subtle pt-6">
              <h3 className="text-lg font-semibold text-text-primary mb-1">Rules</h3>
              <p className="text-xs text-text-tertiary mb-3">
                Shown to people requesting to join. Leave blank for none.
              </p>
              <Textarea
                value={rules}
                onChange={(e) => setRules(e.target.value)}
                placeholder="e.g. Be respectful. Keep self-promo to the right channel."
                disabled={!canEdit}
                className="bg-surface-3 border-border-default text-text-primary min-h-[90px]"
              />
            </div>

            {/* Requests — owner/admin. Pending join requests with approve/deny. */}
            {canEdit && (
              <div className="border-t border-border-subtle pt-6">
                <h3 className="text-lg font-semibold text-text-primary mb-1 flex items-center gap-2">
                  Requests
                  {pendingRequests.length > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold bg-accent text-text-on-accent">
                      {pendingRequests.length}
                    </span>
                  )}
                </h3>
                <p className="text-xs text-text-tertiary mb-3">Pending join requests.</p>

                {requestsError && (
                  <div
                    role="alert"
                    className="mb-2 px-3 py-2 rounded-sm text-xs bg-status-busy/10 border border-status-busy/30 text-status-busy"
                  >
                    {requestsError}
                  </div>
                )}

                {!requestsLoaded ? (
                  <p className="text-xs text-text-tertiary">Loading…</p>
                ) : pendingRequests.length === 0 ? (
                  <p className="text-xs text-text-tertiary">No pending requests.</p>
                ) : (
                  <ul className="space-y-2">
                    {pendingRequests.map((r) => (
                      <li
                        key={r.id}
                        className="flex items-start gap-3 p-3 rounded-md bg-surface-3 border border-border-subtle"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-text-primary truncate">
                            {r.user?.name || 'Unknown'}
                          </p>
                          {r.message && (
                            <p className="text-xs text-text-secondary mt-0.5 break-words">
                              {r.message}
                            </p>
                          )}
                          <p className="text-[11px] font-mono text-text-tertiary mt-1">
                            {new Date(r.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button
                            onClick={() => decideRequest(r.userId, 'approve')}
                            disabled={busyReqUserId === r.userId}
                            className="h-8 px-3 text-xs bg-accent hover:bg-accent-hover text-text-on-accent"
                          >
                            Approve
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => decideRequest(r.userId, 'deny')}
                            disabled={busyReqUserId === r.userId}
                            className="h-8 px-3 text-xs border-border-default text-text-secondary hover:bg-surface-2"
                          >
                            Deny
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Danger Zone — owner only. Soft-delete only (no Archive). */}
            {isOwner && (
              <div className="border-t border-border-subtle pt-6">
                <h3 className="text-lg font-semibold text-status-busy mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Danger Zone
                </h3>
                <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Group
                </Button>
                <p className="text-xs text-text-tertiary mt-2">
                  Soft-deletes for 30 days, then permanent. Members are notified.
                </p>
              </div>
            )}

            {saveError && (
              <div
                role="alert"
                className="px-3 py-2 rounded-sm text-xs bg-status-busy/10 border border-status-busy/30 text-status-busy"
              >
                {saveError}
              </div>
            )}

            {/* Actions */}
            <div className="flex space-x-3 pt-4 border-t border-border-subtle">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1 border-border-default text-text-secondary hover:bg-surface-3"
              >
                Cancel
              </Button>
              {canEdit && (
                <Button
                  onClick={handleSave}
                  disabled={isSaving || !formData.name.trim() || !formData.description.trim()}
                  className="flex-1 bg-accent hover:bg-accent-hover text-text-on-accent"
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-text-on-accent mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="p-6">
            <h3 className="text-lg font-semibold text-status-busy mb-4 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Delete this group?
            </h3>
            <p className="text-text-secondary mb-2 text-sm">
              This soft-deletes <strong>{group.name}</strong> for 30 days, then it&apos;s permanent.
              Members are notified. The group disappears from listings immediately.
            </p>
            <label className="block text-xs text-text-tertiary mb-1.5">
              Type the group name <span className="font-mono text-text-secondary">{group.name}</span>{' '}
              to confirm.
            </label>
            <Input
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              placeholder={group.name}
              className="bg-surface-3 border-border-default text-text-primary mb-4"
            />

            {deleteError && (
              <div
                role="alert"
                className="mb-4 px-3 py-2 rounded-sm text-xs bg-status-busy/10 border border-status-busy/30 text-status-busy"
              >
                {deleteError}
              </div>
            )}

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteText('');
                  setDeleteError(null);
                }}
                className="flex-1 border-border-default text-text-secondary hover:bg-surface-3"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={!deleteArmed || isDeleting}
                className="flex-1"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Group
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
